import * as THREE from 'three';
import { createCharacterAnimator, type AnimInput, type CharacterAnimator } from './animation';
import type { CharacterStyle, Footwear, Headwear } from './characterStyle';
import { crowdInView, hitFlash } from './characterLook';
import { createCrowdMaterial } from './crowdMaterial';
import { instancedDepthMaterial } from '../instancedDepth';
import { CrowdLimb, Cut, CUT_COUNT, Face, FACE_COUNT, figureLook, footShape, GeoBuilder, handShape, HAT_KINDS, hairCap, hatShape, headBase, headFaces, limbShape, restOffset, Tail, TAIL_DEPTH, TAIL_HEM, tailShape, torsoBase, torsoCut, trimUnit, type Detail, type FigureLook, type Sink } from './figure';
import { createSkeleton, Joint, JOINT_COUNT, JOINT_PARENTS, POSE_HIPS_OFFSET, proportionsFor, type Pose, type Proportions, type Skeleton } from './skeleton';

/**
 * Instanced crowds on the shared rig. Every character on screen shares eleven InstancedMeshes — torso,
 * coat skirt, upper limb segments, head, hair, hats, hands, shoes, small trim (cuffs, glasses, props),
 * lower limb segments and flared trouser shins — each part placed by the shared skeleton's forward
 * kinematics (crowdJointMatrices) and coloured per instance. The shapes come from figure.ts at reference proportions; builds are per-instance scale.
 * Looks that need different geometry (a tunic or lapels, a moustache or a beard, a trilby or a cap,
 * boots or heels, ten hair styles) are variants inside one geometry, picked per instance in the
 * shader (crowdMaterial.ts). Draw calls are fixed whatever the crowd size or its variety, and nothing
 * is allocated per frame.
 *
 * Detail follows the render quality (characterLook.ts): 'high' full faces, fingers and seams; 'mid'
 * (MEDIUM, phones) simpler faces and hands; 'low' as mid without trim and crowd shadows.
 */

export type HatKind = Exclude<Headwear, 'none'>;

/** Offsets (floats) of the colour slots inside CrowdLook.colours. */
const O = { coat: 0, trousers: 3, shoes: 6, skin: 9, hair: 12, shirt: 15, tA: 18, tB: 21, tC: 24, hA: 27, hB: 30, hC: 33, hatA: 36, hatB: 39, hatC: 42, tail: 45, trim: 48 } as const;

/** Everything the renderer needs about one look, precomputed (allocate on spawn, not per frame). */
export interface CrowdLook {
  readonly style: CharacterStyle;
  readonly figure: FigureLook;
  /** Proportions at height 1 (height is applied as root scale). */
  readonly dims: Proportions;
  /** Linear RGB per colour slot (see O). */
  readonly colours: Float32Array;
  /** Coat-skirt length (0 = none) and shape variant. */
  readonly tail: number;
  readonly tailKind: number;
  readonly armColour: number;
  readonly forearmColour: number;
  readonly legScale: number;
  readonly hat: HatKind | null;
  /** Index of `hat` in HAT_KINDS (-1 = bare head). */
  readonly hatIndex: number;
  readonly hair: boolean;
  /** Hair blend weights (volume, length, fringe/quiff, recede). */
  readonly hairBlend: Float32Array;
  readonly cut: number;
  readonly face: number;
  readonly feet: number;
  readonly moustache: boolean;
  readonly uniform: boolean;
  readonly trims: number;
  /** Trim boxes: joint, x, y, z, sx, sy, sz per trim. */
  readonly trimData: Float32Array;
}

const scratchColour = new THREE.Color();

export function createCrowdLook(style: CharacterStyle): CrowdLook {
  const dims = proportionsFor(style.build, 1);
  const f = figureLook(style, dims);
  const p = style.palette;
  const colours = new Float32Array(O.trim + f.trims.length * 3);
  const set = (o: number, hex: number): void => {
    scratchColour.setHex(hex);
    colours[o] = scratchColour.r;
    colours[o + 1] = scratchColour.g;
    colours[o + 2] = scratchColour.b;
  };
  set(O.coat, p.coat);
  set(O.trousers, p.trousers);
  set(O.shoes, p.shoes);
  set(O.skin, p.skin);
  set(O.hair, p.hair);
  set(O.shirt, p.shirt);
  set(O.tA, f.torso[0]);
  set(O.tB, f.torso[1]);
  set(O.tC, f.torso[2]);
  set(O.hA, f.head[0]);
  set(O.hB, f.head[1]);
  set(O.hC, f.head[2]);
  set(O.hatA, f.hatColours[0]);
  set(O.hatB, f.hatColours[1]);
  set(O.hatC, f.hatColours[2]);
  set(O.tail, f.tailColour);
  const trimData = new Float32Array(f.trims.length * 7);
  f.trims.forEach((t, k) => {
    set(O.trim + k * 3, t.colour);
    trimData.set([t.joint, t.x, t.y, t.z, t.sx, t.sy, t.sz], k * 7);
  });
  const hw = style.headwear;
  const blend = new Float32Array(4);
  if (f.hair) blend.set(f.hair);
  return {
    style,
    figure: f,
    dims,
    colours,
    tail: f.tail,
    tailKind: f.tailKind,
    armColour: f.shirtArms ? O.shirt : O.coat,
    forearmColour: f.shirtArms ? O.skin : O.coat,
    legScale: f.skirt ? 0.82 : 1,
    hat: hw === 'none' ? null : hw,
    hatIndex: f.hat,
    hair: f.hair !== null,
    hairBlend: blend,
    cut: f.cut,
    face: f.face,
    feet: f.feet,
    moustache: f.face === Face.moustache || f.face === Face.mutton || f.face === Face.beard,
    uniform: style.outfit === 'tunic' || style.outfit === 'armed',
    trims: f.trims.length,
    trimData,
  };
}

// --- renderer ----------------------------------------------------------------------------------

export interface CrowdRendererOptions {
  name: string;
  capacity: number;
  /** Hat kinds to build (one shared hat mesh; each kind is a variant inside it). */
  hats: readonly HatKind[];
  /** Hair mesh (helmets hide it; peaked caps and bare heads show it). */
  hair: boolean;
  /** Torso cuts: 'jacket' = civilian clothes, 'tunic' = police (tunic and armed blouson). */
  torso: 'jacket' | 'tunic';
  /** Unused since the second pass (the helmet crest is part of the helmet). */
  crest?: boolean;
  /** Trim boxes per character (cuffs, glasses, cigarette, props, holster). */
  blobsPerCharacter: number;
}

/** Limb instances per character: 4 upper segments, 2 lower arms, 2 shins (plain or flared). */
const UPPERS = 4;
const LOWERS = 2;
const K = { torso: 0, tail: 1, limbs: 2, head: 3, hair: 4, hat: 5, hands: 6, feet: 7, trim: 8, lowers: 9, shins: 10 } as const;
const KINDS = 11;
const CIVIL_CUTS = [Cut.lapel, Cut.knit, Cut.zip, Cut.parka, Cut.dress, Cut.waistcoat, Cut.clerical];
const POLICE_CUTS = [Cut.tunic, Cut.armed];
const FOOTWEAR: readonly Footwear[] = ['shoes', 'boots', 'heels'];

interface MeshKind {
  mesh: THREE.InstancedMesh | null;
  geos: [THREE.BufferGeometry, THREE.BufferGeometry] | null;
  /** Instances written this frame. */
  n: number;
  b: Float32Array | null;
  c: Float32Array | null;
  v: Float32Array | null;
  hair: Float32Array | null;
  attrs: THREE.InstancedBufferAttribute[];
}

/** Reference skeleton for re-expressing pieces in the anchor joint of each mesh. */
const REF = createSkeleton('average', 1);

function anchoredSink(anchor: number, b: GeoBuilder): Sink {
  return (joint) => {
    const [x, y, z] = joint === anchor ? [0, 0, 0] : restOffset(REF.rest, joint, anchor);
    b.ox = x;
    b.oy = y;
    b.oz = z;
    return b;
  };
}

export class CrowdRenderer {
  private readonly kinds: MeshKind[] = [];
  /** Incremented by begin(); actors remember the frame and slots they were last written to. */
  private frame = 0;
  private readonly mats: THREE.Material[] = [];
  private readonly jm = new Float32Array(JOINT_COUNT * 16);
  /** Joint matrices of the character being emitted, plus a world offset added to every part. */
  private cur: Float32Array = this.jm;
  private dx = 0;
  private dy = 0;
  private dz = 0;
  /** Hit-flash 0..1 of the character being emitted (characterLook `hitFlash`): its colours pull towards a warm white. */
  private flash = 0;
  private n = 0;
  private detail: Detail | 'low' = 'high';
  /** Arrays of the mesh currently being written (set by `target`). */
  private tk: MeshKind | null = null;
  private mArr: Float32Array = this.jm;
  private cArr: Float32Array | null = null;
  private readonly hatSet: Set<number>;

  constructor(
    readonly group: THREE.Group,
    private readonly opts: CrowdRendererOptions,
  ) {
    const cap = opts.capacity;
    const police = opts.torso === 'tunic';
    this.hatSet = new Set(opts.hats.map((h) => HAT_KINDS.indexOf(h)));
    const build = (fn: (detail: Detail) => GeoBuilder): [THREE.BufferGeometry, THREE.BufferGeometry] => [fn('high').toGeometry(), fn('mid').toGeometry()];

    const torso = build((detail) => {
      const b = new GeoBuilder();
      const sink = anchoredSink(Joint.chest, b);
      torsoBase(sink, REF.dims, detail);
      for (const cut of police ? POLICE_CUTS : CIVIL_CUTS) {
        b.variant = cut;
        torsoCut(sink, cut, REF.dims, detail);
      }
      b.variant = -1;
      b.hasVariants = true;
      return b;
    });
    const tail = build((detail) => {
      const b = new GeoBuilder();
      for (const k of [Tail.coat, Tail.skirt, Tail.seat]) {
        b.variant = k;
        tailShape(b, k, detail);
      }
      return b;
    });
    // Limb segments: three plain meshes (upper segments, lower segments with the round joint, and
    // the flared trouser shin) rather than variants of one, so no collapsed geometry is carried.
    const limb = (kind: number) =>
      build((detail) => {
        const b = new GeoBuilder();
        limbShape(b, kind, detail);
        return b;
      });
    const limbs = limb(CrowdLimb.upper);
    const lowers = limb(CrowdLimb.lower);
    const shins = limb(CrowdLimb.flare);
    const head = build((detail) => {
      const b = new GeoBuilder();
      const sink = anchoredSink(Joint.head, b);
      headBase(sink, REF.dims, detail);
      const faces: number[] = [];
      for (let face = 0; face < FACE_COUNT; face++) if (!police || face !== Face.female) faces.push(face);
      headFaces(sink, faces, REF.dims, detail);
      b.hasVariants = true;
      return b;
    });
    const hair = opts.hair
      ? build((detail) => {
          const b = new GeoBuilder(true);
          hairCap(b, REF.dims, detail);
          return b;
        })
      : null;
    const hats = opts.hats.length
      ? build((detail) => {
          const b = new GeoBuilder();
          for (const kind of opts.hats) {
            b.variant = HAT_KINDS.indexOf(kind);
            hatShape(b, kind, REF.dims, detail);
          }
          b.hasVariants = true;
          return b;
        })
      : null;
    const hands = build((detail) => {
      const b = new GeoBuilder();
      b.variant = 0;
      handShape(b, 1, REF.dims, detail);
      b.variant = 1;
      handShape(b, -1, REF.dims, detail);
      return b;
    });
    const feet = build((detail) => {
      const b = new GeoBuilder();
      FOOTWEAR.forEach((kind, i) => {
        if (police && kind === 'heels') return;
        b.variant = i;
        footShape(b, kind, REF.dims, detail);
      });
      return b;
    });
    const trim = build(() => {
      const b = new GeoBuilder();
      trimUnit(b);
      return b;
    });

    const spec: [MeshKind['geos'], number, { b?: boolean; c?: boolean; variant?: boolean; hair?: boolean }, boolean][] = [
      [torso, cap, { b: true, c: true, variant: true }, true],
      [tail, cap, { variant: true }, false],
      [limbs, cap * UPPERS, {}, true],
      [head, cap, { b: true, c: true, variant: true }, false],
      [hair, cap, { hair: true }, false],
      [hats, cap, { b: true, c: true, variant: true }, false],
      [hands, cap * 2, { variant: true }, false],
      [feet, cap * 2, { variant: true }, false],
      [trim, cap * opts.blobsPerCharacter, {}, false],
      [lowers, cap * (LOWERS + 2), {}, true],
      [shins, cap * LOWERS, {}, true],
    ];
    for (const [geos, count, mo, shadow] of spec) {
      const kind: MeshKind = { mesh: null, geos, n: 0, b: null, c: null, v: null, hair: null, attrs: [] };
      this.kinds.push(kind);
      if (!geos) continue;
      const { material, depth } = createCrowdMaterial(mo);
      this.mats.push(material, depth);
      const attr = (name: string, size: number): Float32Array => {
        const a = new THREE.InstancedBufferAttribute(new Float32Array(count * size), size);
        a.setUsage(THREE.DynamicDrawUsage);
        for (const g of geos) g.setAttribute(name, a);
        kind.attrs.push(a);
        return a.array as Float32Array;
      };
      if (mo.b) kind.b = attr('iColB', 3);
      if (mo.c) kind.c = attr('iColC', 3);
      if (mo.variant) kind.v = attr('iVar', 1);
      if (mo.hair) kind.hair = attr('iHair', 4);
      const m = new THREE.InstancedMesh(geos[0], material, count);
      m.frustumCulled = false;
      m.count = 0;
      m.visible = false;
      m.name = `${opts.name}-instances`;
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      m.setColorAt(0, scratchColour.set(0xffffff)); // allocates instanceColor
      m.instanceColor!.setUsage(THREE.DynamicDrawUsage);
      m.castShadow = shadow;
      if (shadow && mo.variant) m.customDepthMaterial = depth;
      // The limb batches too: through three's shared depth material every plain-mesh / instanced flip of
      // the shadow pass re-fetched its program (render/instancedDepth.ts).
      else if (shadow) m.customDepthMaterial = instancedDepthMaterial(true);
      kind.mesh = m;
      group.add(m);
    }
  }

  /** Draw-call count of this renderer when every mesh kind is in use (main pass). */
  get meshCount(): number {
    return this.kinds.filter((k) => k.mesh).length;
  }

  /** 'high' full detail, 'mid' simpler faces/hands/seams, 'low' as mid without trim and shadows. */
  setDetail(level: Detail | 'low'): void {
    if (level === this.detail) return;
    this.detail = level;
    const geo = level === 'high' ? 0 : 1;
    this.kinds.forEach((k, i) => {
      if (!k.mesh || !k.geos) return;
      k.mesh.geometry = k.geos[geo];
      if (i === K.torso || i === K.limbs || i === K.lowers || i === K.shins) k.mesh.castShadow = level !== 'low';
    });
  }

  begin(): void {
    this.n = 0;
    for (const k of this.kinds) k.n = 0;
    this.frame++;
  }

  /**
   * Add one character: `skeleton` must be at height 1 for the look's build (the animator's own
   * skeleton is); height and visual scale go into `scale`.
   */
  add(look: CrowdLook, skeleton: Skeleton, pose: Pose, x: number, y: number, z: number, yaw: number, scale: number): void {
    if (this.n >= this.opts.capacity || !crowdInView(x, y, z)) return;
    crowdJointMatrices(skeleton, pose, this.jm, x, y, z, yaw, scale);
    this.cur = this.jm;
    this.dx = this.dy = this.dz = 0;
    this.emit(look);
  }

  /** Add an actor posed by poseActor(), drawn at (x, y, z): its cached matrices shifted by the move since. */
  addActor(a: CrowdActor, x: number, y: number, z: number): void {
    // Off screen (characterLook's view cull): not drawn, nor shadowed; its next write is a full one.
    if (this.n >= this.opts.capacity || !crowdInView(x, y, z)) return;
    const dx = x - a.jx;
    const dy = y - a.jy;
    const dz = z - a.jz;
    const kinds = this.kinds;
    // The hit-flash rides on the instance colours (no extra draw call, so LOW's crowd flashes too): a pulse
    // and the frame after it are full writes.
    const flash = hitFlash(a.hitT, a.hitStrength);
    let same = !a.dirty && a.emitFrame === this.frame - 1 && flash === 0 && a.flashed === 0;
    if (same) for (let k = 0; k < KINDS; k++) if (a.slots[k] !== kinds[k]!.n) same = false;
    if (same) {
      // Same pose and the same instance slots as last frame: only the root moved, shift the parts.
      const ddx = dx - a.edx;
      const ddy = dy - a.edy;
      const ddz = dz - a.edz;
      for (let k = 0; k < KINDS; k++) {
        const kind = kinds[k]!;
        const c = a.counts[k]!;
        if (c > 0 && kind.mesh) shift(kind.mesh, kind.n, c, ddx, ddy, ddz);
        kind.n += c;
      }
      this.n++;
    } else {
      for (let k = 0; k < KINDS; k++) a.slots[k] = kinds[k]!.n;
      this.cur = a.jm;
      this.dx = dx;
      this.dy = dy;
      this.dz = dz;
      this.flash = flash;
      this.emit(a.look);
      this.flash = 0;
      for (let k = 0; k < KINDS; k++) a.counts[k] = kinds[k]!.n - a.slots[k]!;
      a.dirty = false;
    }
    a.emitFrame = this.frame;
    a.flashed = flash;
    a.edx = dx;
    a.edy = dy;
    a.edz = dz;
  }

  private emit(look: CrowdLook): void {
    const d = look.dims;
    const col = look.colours;
    this.n++;
    const w = d.width;
    const dep = d.depth;
    const L = d.limb;
    const kinds = this.kinds;

    this.target(kinds[K.torso]!);
    this.part(Joint.chest, 0, 0, 0, w, 1, dep, col, O.tA, O.tB, O.tC, look.cut);
    if (look.tail > 0) {
      const hem = d.waistRadius * TAIL_HEM;
      this.target(kinds[K.tail]!);
      this.part(Joint.coatTail, 0, -0.02, 0, hem, look.tail, hem * TAIL_DEPTH * dep, col, O.tail, -1, -1, look.tailKind);
    }

    this.target(kinds[K.limbs]!);
    const ua = (look.figure.shirtArms ? 0.066 : 0.07) * L;
    const la = (look.figure.shirtArms ? 0.058 : 0.062) * L;
    const ul = 0.088 * w * look.legScale;
    const ll = 0.068 * w * look.legScale;
    this.part(Joint.upperArmL, 0, 0, 0, ua, d.upperArm, ua, col, look.armColour);
    this.part(Joint.upperArmR, 0, 0, 0, ua, d.upperArm, ua, col, look.armColour);
    this.part(Joint.upperLegL, 0, 0, 0, ul, d.upperLeg + 0.02, ul, col, O.trousers);
    this.part(Joint.upperLegR, 0, 0, 0, ul, d.upperLeg + 0.02, ul, col, O.trousers);
    // Lower segments: forearms always on the lower mesh; shins on it under a skirt (tights, no
    // flare), on the flared mesh in trousers.
    this.target(kinds[K.lowers]!);
    this.part(Joint.lowerArmL, 0, 0, 0, la, d.lowerArm, la, col, look.forearmColour);
    this.part(Joint.lowerArmR, 0, 0, 0, la, d.lowerArm, la, col, look.forearmColour);
    this.target(kinds[look.figure.skirt ? K.lowers : K.shins]!);
    this.part(Joint.lowerLegL, 0, 0, 0, ll, d.lowerLeg, ll, col, O.trousers);
    this.part(Joint.lowerLegR, 0, 0, 0, ll, d.lowerLeg, ll, col, O.trousers);

    this.target(kinds[K.head]!);
    this.part(Joint.head, 0, 0, 0, 1, 1, 1, col, O.hA, O.hB, O.hC, look.face);
    const hairKind = kinds[K.hair]!;
    if (look.hair && hairKind.mesh) {
      this.target(hairKind);
      const i = hairKind.n;
      this.part(Joint.head, 0, 0, 0, 1, 1, 1, col, O.hair);
      hairKind.hair!.set(look.hairBlend, i * 4);
    }
    if (look.hatIndex >= 0 && this.hatSet.has(look.hatIndex)) {
      this.target(kinds[K.hat]!);
      this.part(Joint.head, 0, 0, 0, 1, 1, 1, col, O.hatA, O.hatB, O.hatC, look.hatIndex);
    }

    this.target(kinds[K.hands]!);
    this.part(Joint.handL, 0, 0, 0, L, L, L, col, O.skin, -1, -1, 0);
    this.part(Joint.handR, 0, 0, 0, L, L, L, col, O.skin, -1, -1, 1);
    this.target(kinds[K.feet]!);
    this.part(Joint.footL, 0, 0, 0, 1, 1, 1, col, O.shoes, -1, -1, look.feet);
    this.part(Joint.footR, 0, 0, 0, 1, 1, 1, col, O.shoes, -1, -1, look.feet);

    if (this.detail !== 'low' && look.trims > 0) {
      const trim = kinds[K.trim]!;
      this.target(trim);
      const max = this.n * this.opts.blobsPerCharacter;
      const t = look.trimData;
      for (let k = 0; k < look.trims && trim.n < max; k++) {
        const o = k * 7;
        this.part(t[o]!, t[o + 1]!, t[o + 2]!, t[o + 3]!, t[o + 4]!, t[o + 5]!, t[o + 6]!, col, O.trim + k * 3);
      }
    }
  }

  end(): void {
    for (const k of this.kinds) if (k.mesh) this.setCount(k, k.n);
  }

  dispose(): void {
    for (const k of this.kinds) {
      if (!k.mesh) continue;
      k.mesh.removeFromParent();
      k.mesh.dispose();
      for (const g of k.geos!) g.dispose();
    }
    for (const m of this.mats) m.dispose();
  }

  private setCount(k: MeshKind, count: number): void {
    const m = k.mesh!;
    m.count = count;
    // A zero-instance InstancedMesh would still cost a (useless) draw call.
    m.visible = count > 0;
    if (count === 0) return;
    m.instanceMatrix.needsUpdate = true;
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
    for (const a of k.attrs) a.needsUpdate = true;
  }

  private target(k: MeshKind): void {
    this.tk = k;
    this.mArr = k.mesh ? (k.mesh.instanceMatrix.array as Float32Array) : this.jm;
    this.cArr = k.mesh?.instanceColor ? (k.mesh.instanceColor.array as Float32Array) : null;
  }

  /**
   * Next instance of the target mesh = jointMatrix · T(ox, oy, oz) · S(sx, sy, sz), coloured from
   * `colours` (channel A, optional B / C) with variant `v`.
   */
  private part(joint: number, ox: number, oy: number, oz: number, sx: number, sy: number, sz: number, colours: Float32Array, a: number, b = -1, c = -1, v = -1): void {
    const k = this.tk!;
    if (!k.mesh) return;
    const index = k.n++;
    const m = this.cur;
    const j = joint * 16;
    const out = this.mArr;
    const o = index * 16;
    out[o] = m[j]! * sx;
    out[o + 1] = m[j + 1]! * sx;
    out[o + 2] = m[j + 2]! * sx;
    out[o + 3] = 0;
    out[o + 4] = m[j + 4]! * sy;
    out[o + 5] = m[j + 5]! * sy;
    out[o + 6] = m[j + 6]! * sy;
    out[o + 7] = 0;
    out[o + 8] = m[j + 8]! * sz;
    out[o + 9] = m[j + 9]! * sz;
    out[o + 10] = m[j + 10]! * sz;
    out[o + 11] = 0;
    out[o + 12] = m[j]! * ox + m[j + 4]! * oy + m[j + 8]! * oz + m[j + 12]! + this.dx;
    out[o + 13] = m[j + 1]! * ox + m[j + 5]! * oy + m[j + 9]! * oz + m[j + 13]! + this.dy;
    out[o + 14] = m[j + 2]! * ox + m[j + 6]! * oy + m[j + 10]! * oz + m[j + 14]! + this.dz;
    out[o + 15] = 1;
    const i3 = index * 3;
    const cArr = this.cArr;
    if (cArr) {
      cArr[i3] = colours[a]!;
      cArr[i3 + 1] = colours[a + 1]!;
      cArr[i3 + 2] = colours[a + 2]!;
    }
    if (k.b && b >= 0) {
      k.b[i3] = colours[b]!;
      k.b[i3 + 1] = colours[b + 1]!;
      k.b[i3 + 2] = colours[b + 2]!;
    }
    if (k.c && c >= 0) {
      k.c[i3] = colours[c]!;
      k.c[i3 + 1] = colours[c + 1]!;
      k.c[i3 + 2] = colours[c + 2]!;
    }
    if (k.v) k.v[index] = v;
    if (this.flash > 0) {
      if (cArr) flashRgb(cArr, i3, this.flash);
      if (k.b && b >= 0) flashRgb(k.b, i3, this.flash);
      if (k.c && c >= 0) flashRgb(k.c, i3, this.flash);
    }
  }
}

/** The hit-flash's warm white (linear), as the detailed figures' (detailedPlayer `uHit`). */
const FLASH_R = 1;
const FLASH_G = 0.9;
const FLASH_B = 0.72;

/** Pull the colour at `arr[i..i+2]` towards the flash white by `k`. */
function flashRgb(arr: Float32Array, i: number, k: number): void {
  arr[i] = arr[i]! + (FLASH_R - arr[i]!) * k;
  arr[i + 1] = arr[i + 1]! + (FLASH_G - arr[i + 1]!) * k;
  arr[i + 2] = arr[i + 2]! + (FLASH_B - arr[i + 2]!) * k;
}

/** Add (dx, dy, dz) to the translation of `count` instances starting at `start`. */
function shift(m: THREE.InstancedMesh, start: number, count: number, dx: number, dy: number, dz: number): void {
  const arr = m.instanceMatrix.array as Float32Array;
  for (let k = start, end = start + count; k < end; k++) {
    const o = k * 16;
    arr[o + 12] = arr[o + 12]! + dx;
    arr[o + 13] = arr[o + 13]! + dy;
    arr[o + 14] = arr[o + 14]! + dz;
  }
}

/**
 * Same result as writeJointMatrices(skeleton, pose, out, 0, root) — world matrices, one 16-float slot
 * per joint — but tuned for crowds: Float32 throughout, only the 3×4 affine part is computed (the
 * bottom row of each slot is left as is) and sin/cos are skipped for zero angles, which most joint
 * axes are. ~2× faster; the crowd renderer runs it for every character every frame.
 */
export function crowdJointMatrices(skeleton: Skeleton, pose: Pose, out: Float32Array, x: number, y: number, z: number, yaw: number, scale: number): void {
  const rest = skeleton.rest;
  for (let j = 0; j < JOINT_COUNT; j++) {
    const r = j * 3;
    const rx = pose[r]!;
    const ry = pose[r + 1]!;
    const rz = pose[r + 2]!;
    const a = rx === 0 ? 1 : Math.cos(rx);
    const b = rx === 0 ? 0 : Math.sin(rx);
    const c = ry === 0 ? 1 : Math.cos(ry);
    const d = ry === 0 ? 0 : Math.sin(ry);
    const e = rz === 0 ? 1 : Math.cos(rz);
    const f = rz === 0 ? 0 : Math.sin(rz);
    // Local rotation columns (three.js Euler XYZ).
    const ae = a * e;
    const af = a * f;
    const be = b * e;
    const bf = b * f;
    const l00 = c * e;
    const l01 = af + be * d;
    const l02 = bf - ae * d;
    const l10 = -c * f;
    const l11 = ae - bf * d;
    const l12 = be + af * d;
    const l20 = d;
    const l21 = -b * c;
    const l22 = a * c;
    let tx = rest[r]!;
    let ty = rest[r + 1]!;
    let tz = rest[r + 2]!;
    const o = j * 16;
    const parent = JOINT_PARENTS[j]!;
    if (parent < 0) {
      tx += pose[POSE_HIPS_OFFSET]!;
      ty += pose[POSE_HIPS_OFFSET + 1]!;
      tz += pose[POSE_HIPS_OFFSET + 2]!;
      // Root: T(x, y, z) · Ry(yaw) · S(scale).
      const cy = Math.cos(yaw) * scale;
      const sy = Math.sin(yaw) * scale;
      out[o] = cy * l00 + sy * l02;
      out[o + 1] = scale * l01;
      out[o + 2] = -sy * l00 + cy * l02;
      out[o + 4] = cy * l10 + sy * l12;
      out[o + 5] = scale * l11;
      out[o + 6] = -sy * l10 + cy * l12;
      out[o + 8] = cy * l20 + sy * l22;
      out[o + 9] = scale * l21;
      out[o + 10] = -sy * l20 + cy * l22;
      out[o + 12] = cy * tx + sy * tz + x;
      out[o + 13] = scale * ty + y;
      out[o + 14] = -sy * tx + cy * tz + z;
    } else {
      const p = parent * 16;
      const p0 = out[p]!, p1 = out[p + 1]!, p2 = out[p + 2]!;
      const p4 = out[p + 4]!, p5 = out[p + 5]!, p6 = out[p + 6]!;
      const p8 = out[p + 8]!, p9 = out[p + 9]!, p10 = out[p + 10]!;
      out[o] = p0 * l00 + p4 * l01 + p8 * l02;
      out[o + 1] = p1 * l00 + p5 * l01 + p9 * l02;
      out[o + 2] = p2 * l00 + p6 * l01 + p10 * l02;
      out[o + 4] = p0 * l10 + p4 * l11 + p8 * l12;
      out[o + 5] = p1 * l10 + p5 * l11 + p9 * l12;
      out[o + 6] = p2 * l10 + p6 * l11 + p10 * l12;
      out[o + 8] = p0 * l20 + p4 * l21 + p8 * l22;
      out[o + 9] = p1 * l20 + p5 * l21 + p9 * l22;
      out[o + 10] = p2 * l20 + p6 * l21 + p10 * l22;
      out[o + 12] = p0 * tx + p4 * ty + p8 * tz + out[p + 12]!;
      out[o + 13] = p1 * tx + p5 * ty + p9 * tz + out[p + 13]!;
      out[o + 14] = p2 * tx + p6 * ty + p10 * tz + out[p + 14]!;
    }
  }
}

// --- animated actors ---------------------------------------------------------------------------

/** One animated crowd member: its look and its own animator (pose state persists across frames). */
export interface CrowdActor {
  key: number;
  look: CrowdLook;
  animator: CharacterAnimator;
  lastYaw: number;
  /** Rendered heading: eases toward the sim heading so sim-side snaps (flee, get-up) don't pop. */
  drawYaw: number;
  lastX: number;
  lastZ: number;
  /** Frame stamp of the last update that touched this actor. */
  seen: number;
  /** Fall direction latched at knockdown (+1 = forwards: the lying pose is drawn turned around). */
  fallFlip: number;
  /** Cached joint matrices from the last pose update and the root position they were built at. */
  readonly jm: Float32Array;
  jx: number;
  jy: number;
  jz: number;
  /** Animation time not yet fed to the animator (pose LOD skips frames). */
  pendingDt: number;
  posed: boolean;
  /** Visual scale the animator's stride maths was built for. */
  readonly animScale: number;
  /** Renderer bookkeeping: pose changed since the last write, and the frame/slots/offset of that write. */
  dirty: boolean;
  emitFrame: number;
  /** First instance slot and instance count per mesh kind at the last full write. */
  readonly slots: Int32Array;
  readonly counts: Int32Array;
  edx: number;
  edy: number;
  edz: number;
  /** View-side extras (hit reaction clock, look target); owned by the view that drives the actor. */
  hitT: number;
  hitDir: number;
  hitStrength: number;
  /** Hit-flash the actor was last written with (renderer bookkeeping: the frame after a pulse rewrites it). */
  flashed: number;
  /**
   * Meshy crowd (crowdBodies.ts): the skeleton the pose is written through (a body's proportions; null = the
   * animator's own), the body index (-2 = not chosen yet, -1 = none) and its tint colours. Owned by the view.
   */
  skel: Skeleton | null;
  body: number;
  tint: Float32Array | null;
}

/**
 * Advance the actor's animator and rebuild its joint matrices — or, when `full` is false and it has
 * been posed before, only bank the frame time: the renderer then reuses the cached matrices shifted by
 * the root's movement (addActor). Crowds alternate actors between frames to halve animation cost.
 * `input.dt` is consumed (overwritten).
 */
export function poseActor(a: CrowdActor, input: AnimInput, full: boolean, x: number, y: number, z: number, yaw: number, scale: number): void {
  a.pendingDt += input.dt;
  if (!full && a.posed) return;
  input.dt = Math.min(a.pendingDt, 0.1);
  a.pendingDt = 0;
  a.posed = true;
  a.dirty = true;
  const pose = a.animator.update(input);
  crowdJointMatrices(a.skel ?? a.animator.skeleton, pose, a.jm, x, y, z, yaw, scale);
  a.jx = x;
  a.jy = y;
  a.jz = z;
}

/**
 * Keyed pool of animated actors. A new key reuses a retired actor of the same build (animator reset)
 * or creates one (bounded by the crowd cap); keys not seen in a frame retire their actor. The
 * per-frame path allocates nothing.
 */
export class CrowdActorPool {
  private readonly byKey = new Map<number, CrowdActor>();
  private readonly active: CrowdActor[] = [];
  private readonly free: CrowdActor[] = [];
  private frame = 0;

  begin(): void {
    this.frame++;
  }

  get(key: number): CrowdActor | undefined {
    const a = this.byKey.get(key);
    if (a) a.seen = this.frame;
    return a;
  }

  /** Nearest unclaimed actor to (x, z) within `radius` (for sims that expose no stable id). */
  nearest(x: number, z: number, radius: number): CrowdActor | undefined {
    let best: CrowdActor | undefined;
    let bestD = radius * radius;
    for (const a of this.active) {
      if (a.seen === this.frame) continue;
      const dx = a.lastX - x;
      const dz = a.lastZ - z;
      const d = dx * dx + dz * dz;
      if (d < bestD) {
        bestD = d;
        best = a;
      }
    }
    if (best) best.seen = this.frame;
    return best;
  }

  /** Every live actor (read-only; for views that react to events by position). */
  get actors(): readonly CrowdActor[] {
    return this.active;
  }

  create(key: number, look: CrowdLook, visualScale: number, x: number, z: number, yaw: number): CrowdActor {
    let a: CrowdActor | undefined;
    for (let i = this.free.length - 1; i >= 0; i--) {
      const f = this.free[i]!;
      if (f.animator.skeleton.build !== look.style.build || f.animScale !== visualScale) continue;
      this.free[i] = this.free[this.free.length - 1]!;
      this.free.pop();
      a = f;
      break;
    }
    if (a) {
      a.animator.reset('idle');
      a.key = key;
      a.look = look;
      a.lastYaw = a.drawYaw = yaw;
      a.lastX = a.jx = x;
      a.lastZ = a.jz = z;
      a.jy = 0;
      a.seen = this.frame;
      a.fallFlip = 0;
      a.pendingDt = 0;
      a.posed = false;
      a.dirty = true;
      a.emitFrame = -1;
      a.hitT = 99;
      a.hitStrength = 0;
      a.skel = null;
      a.body = -2;
    } else {
      const animator = createCharacterAnimator({ build: look.style.build, height: 1, scale: visualScale, seed: key * 2654435761 });
      a = {
        key,
        look,
        animator,
        lastYaw: yaw,
        drawYaw: yaw,
        lastX: x,
        lastZ: z,
        seen: this.frame,
        fallFlip: 0,
        jm: new Float32Array(JOINT_COUNT * 16),
        jx: x,
        jy: 0,
        jz: z,
        pendingDt: 0,
        posed: false,
        animScale: visualScale,
        dirty: true,
        emitFrame: -1,
        slots: new Int32Array(KINDS).fill(-1),
        counts: new Int32Array(KINDS),
        edx: 0,
        edy: 0,
        edz: 0,
        hitT: 99,
        hitDir: 0,
        hitStrength: 0,
        flashed: 0,
        skel: null,
        body: -2,
        tint: null,
      };
    }
    a.animator.setTraits(traitsOf(look));
    this.byKey.set(key, a);
    this.active.push(a);
    return a;
  }

  /** Drop actors not seen since begin(). */
  end(): void {
    for (let i = this.active.length - 1; i >= 0; i--) {
      const a = this.active[i]!;
      if (a.seen === this.frame) continue;
      this.byKey.delete(a.key);
      this.active[i] = this.active[this.active.length - 1]!;
      this.active.pop();
      if (this.free.length < 128) this.free.push(a);
    }
  }

  get size(): number {
    return this.active.length;
  }
}

/** Animator traits (gait, posture, idle habits) from a look. */
export function traitsOf(look: CrowdLook): { age: number; female: boolean; smoker: boolean; swagger: number; bulk: number } {
  return { age: look.figure.age, female: look.figure.female, smoker: !!look.style.smoker, swagger: look.style.swagger ?? 0, bulk: bulkOf(look.style.build) };
}

/** Bulk trait from the build (heavy men plant their feet and carry their arms wide). */
export function bulkOf(build: CharacterStyle['build']): number {
  return build === 'heavy' ? 1 : build === 'stocky' ? 0.45 : 0;
}

/** Ease the actor's rendered heading toward `target` (rad) at `rate` (1/s); returns it. */
export function easeYaw(a: CrowdActor, target: number, rate: number, dt: number): number {
  a.drawYaw = wrapAngle(a.drawYaw + wrapAngle(target - a.drawYaw) * (1 - Math.exp(-rate * dt)));
  return a.drawYaw;
}

/** Wrap an angle difference into (-π, π]. */
export function wrapAngle(a: number): number {
  const t = Math.PI * 2;
  a %= t;
  if (a > Math.PI) a -= t;
  else if (a <= -Math.PI) a += t;
  return a;
}

/** For tests: hat variant ids and the full cut count. */
export const CROWD_INTERNALS = { HAT_KINDS, CUT_COUNT } as const;
